import React, { useState, useEffect, useMemo } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation } from 'swiper'
import 'react-medium-image-zoom/dist/styles.css'
import MyImage from '../../atoms/MyImage/MyImage'
import { getAltText } from '../../../utils/safe'
import NewModal from '../NewModal/NewModal'
import { useSelector } from 'react-redux'
import { Tag } from '../../atoms'
import Wishlist from '../../atoms/WishList/WishList'
import { Download, Zoom } from '../../atoms/NewIcons'
import useTrans from '../../../hooks/useTrans'
import CarouselCertificazioni from "../CarouselCertificazioni"

SwiperCore.use([Navigation])

const MainImage = ({ image, title, isZoom = false, onZoomToggle, children }) => {
  if (!image?.url) return null

  return (
    <div className="prodotto-gallery__main-image__wrapper">
      <div className="prodotto-gallery__main-image">
        <MyImage
          className={
            !isZoom
              ? 'prodotto-gallery__main-image__selected'
              : 'prodotto-gallery__main-image__unselected'
          }
          src={image.url}
          alt={getAltText(image) || title}
          title={title}
          width={1000}
          height={1000}
          priority={true}
          loading="eager"
          onClick={onZoomToggle}
        />
        {children}
      </div>
    </div>
  )
}

const Carousel = ({ variant, images, selectedImageUrl, onImageSelect, imageTitle }) => {
  if (!images || images.length === 0) {
    return null
  }

  return (
    <div
      className={`prodotto-gallery__carousel ${images.length > 3 ? 'prodotto-gallery__carousel--nav' : ''
        }`}
    >
      <button
        className={`prodotto-gallery__carousel__nav prodotto-gallery__carousel__nav--prev ${variant ? `prodotto-gallery__carousel__nav--prev--${variant}` : ''
          }`}
      />
      <button
        className={`prodotto-gallery__carousel__nav prodotto-gallery__carousel__nav--next ${variant ? `prodotto-gallery__carousel__nav--next--${variant}` : ''
          }`}
      />
      <div className="prodotto-gallery__carousel__slider">
        <Swiper
          slidesPerView="auto"
          watchOverflow
          spaceBetween={16}
          navigation={{
            prevEl: `.prodotto-gallery__carousel__nav--prev${variant ? `--${variant}` : ''}`,
            nextEl: `.prodotto-gallery__carousel__nav--next${variant ? `--${variant}` : ''}`,
            disabledClass: 'prodotto-gallery__carousel__nav--disabled',
          }}
          breakpoints={{
            992: {
              direction: 'vertical',
              slidesPerView: 3,
            },
          }}
        >
          {images.map((image) => (
            <SwiperSlide key={image.id || image.url}>
              <div
                className={`prodotto-gallery__carousel__image-box ${selectedImageUrl === image.url
                  ? 'prodotto-gallery__carousel__image-box--active'
                  : ''
                  }`}
                onClick={() => onImageSelect(image)}
              >
                <MyImage
                  src={image.url}
                  alt={getAltText(image) || imageTitle}
                  width={96}
                  height={96}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
}

const ProdottoGallery = ({
  prodotto,
  selectedArticolo,
  selectedImage,
  setSelectedImage,
  wishlist,
  handleWishlist,
  certificazioni = [],
}) => {
  const t = useTrans()
  const { service_gift } = useSelector((state) => state.servicegift)
  const { utente } = useSelector((state) => state?.utente)
  const [isZoomModalVisible, setZoomModalVisible] = useState(false)

  const selectedImagePathname = useMemo(
    () => (selectedImage?.url ? new URL(selectedImage.url)?.pathname : null),
    [selectedImage]
  )

  const certificationsNumber = certificazioni.length
  const hasOverlayCertificazioni = certificationsNumber > 0 && certificationsNumber < 2
  const hasInlineCertificazioni = certificationsNumber >= 2
  
  return (
    <>
      <NewModal
        className="prodotto-gallery__zoom"
        visible={isZoomModalVisible}
        onClose={() => setZoomModalVisible(false)}
      >
        <MainImage
          isZoom
          image={selectedImage}
          title={selectedArticolo?.titolo}
          onZoomToggle={() => setZoomModalVisible((prev) => !prev)}
        />
        <Carousel
          variant="zoom"
          images={selectedArticolo?.immagini}
          selectedImageUrl={selectedImage?.url}
          onImageSelect={(image) => setSelectedImage(image)}
          imageTitle={selectedArticolo?.titolo}
        />
      </NewModal>
      <div className='prodotto-gallery__container'>
        <div className='prodotto-gallery__main'>
          <MainImage
            image={selectedImage}
            title={selectedArticolo?.titolo}
            onZoomToggle={() => setZoomModalVisible((prev) => !prev)}
          >

            {prodotto.brand?.image && (
              <img
                src={prodotto.brand?.image?.url}
                className="prodotto-gallery__main-image__brand"
                alt={getAltText(prodotto.brand?.image)}
              />
            )}
            <span className="prodotto-gallery__main-image__tag">
              {prodotto.trend && (
                <Tag
                  variant={prodotto.trend.codice}
                  colore={prodotto.trend.colore}
                  label={prodotto.trend.nome}
                  isBrand={false}
                />
              )}
            </span>
            {!service_gift && selectedImagePathname && (
              <span className="prodotto-gallery__main-image__download">
                <a href={selectedImagePathname} title={t('Download immagine')} download target="_blank">
                  <Download />
                </a>
              </span>
            )}
            {utente && (
              <button
                className="prodotto-gallery__main-image__wishlist"
                onClick={() => handleWishlist()}
              >
                <Wishlist isActive={wishlist} />
              </button>
            )}
            <button
              className="prodotto-gallery__main-image__zoom"
              onClick={() => setZoomModalVisible((prev) => !prev)}
            >
              <Zoom />
            </button>
            {hasOverlayCertificazioni && (
              <CarouselCertificazioni
                certificazioni={certificazioni}
                className="prodotto-gallery__list__certificazioni"
              />
            )}
          </MainImage>

          <Carousel
            images={selectedArticolo?.immagini}
            selectedImageUrl={selectedImage?.url}
            onImageSelect={(image) => setSelectedImage(image)}
            imageTitle={selectedArticolo?.titolo}
          />
        </div>
        {hasInlineCertificazioni && <CarouselCertificazioni
          certificazioni={certificazioni}
          className="prodotto-gallery__certificazioni-inline"
        />}
      </div>
    </>
  )
}

export default ProdottoGallery
